/**
 * Per-SmartBox maintenance ticket history card.
 *
 * Resolved tickets are hidden by default everywhere else in the UI —
 * the boxes.json `open_ticket_id` field only carries open / in-progress
 * tickets, the Outages page only lists boxes with open work, and so
 * on. This card is the deliberate exception: it lives on the per-box
 * SignalDetail page and surfaces the FULL history (open + resolved +
 * closed) so an operator triaging a specific box can see "what's been
 * fixed here before, by whom, when."
 *
 * Layout:
 *   - Always-visible: list of OPEN / IN_PROGRESS tickets (the "active"
 *     section). Usually 0-2 rows.
 *   - Collapsed by default: the resolved/closed history.  An expand
 *     button reveals it on demand.
 *
 * Fetched on mount via /api/boxes/{scu}/tickets (operator.triage gated).
 */
import React, { useEffect, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import {
  fetchBoxTickets,
  BoxDetailApiError,
  type BoxTicketHistoryEntry,
} from "../../services/boxDetailApi";

const STATUS_BADGE: Record<string, string> = {
  open: "bg-error-100 text-error-800 border-error-300",
  in_progress: "bg-warning-100 text-warning-800 border-warning-300",
  resolved: "bg-success-100 text-success-800 border-success-300",
  closed: "bg-secondary-200 text-secondary-700 border-secondary-300",
};

function StatusBadge({ status }: { status: string }): React.ReactElement {
  const cls = STATUS_BADGE[status] ?? "bg-secondary-100 text-secondary-700 border-secondary-300";
  return (
    <span
      className={
        "inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold border " +
        cls
      }
    >
      {status}
    </span>
  );
}

function TicketRow(
  { t, boxScu }: { t: BoxTicketHistoryEntry; boxScu: string },
): React.ReactElement {
  const mttrResolved =
    t.mttr_resolved_utc !== null && t.status !== "resolved" && t.status !== "closed";
  // Ticket opened on a SCU since swapped out — the ticket follows the smartbox.
  const fromPriorScu = t.scu_name != null && t.scu_name !== boxScu;
  return (
    <li className="px-2 py-2 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <code className="font-mono font-semibold text-primary-700">
          {t.ticket_origin} #{t.ticket_id}
        </code>
        <StatusBadge status={t.status} />
        {fromPriorScu && (
          <span
            className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-secondary-100 text-secondary-600 border border-secondary-200"
            title="Opened on a SCU since swapped out — tickets follow the smartbox"
          >
            on {t.scu_name}
          </span>
        )}
        {mttrResolved && (
          <span
            className="inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold border bg-success-50 text-success-700 border-success-200"
            title={`MTTR overlay marked this source ticket resolved by ${
              t.mttr_resolved_by ?? "?"
            } at ${fmtUtc(t.mttr_resolved_utc)} (source DB row stays untouched)`}
          >
            MTTR resolved
          </span>
        )}
        {t.priority && t.priority !== "normal" && (
          <span className="text-[10px] uppercase font-semibold text-secondary-600">
            {t.priority}
          </span>
        )}
        {t.issue_type && (
          <span className="text-secondary-500 font-mono">{t.issue_type}</span>
        )}
        <span className="ml-auto text-secondary-500" title="Opened">
          opened {fmtUtc(t.date_created)}
        </span>
      </div>
      {t.summary && (
        <div className="text-secondary-800 mt-1">{t.summary}</div>
      )}
      {(t.date_resolved || t.resolution_notes) && (
        <div className="text-secondary-500 mt-1">
          {t.date_resolved && (
            <>
              resolved {fmtUtc(t.date_resolved)}
              {t.resolved_by ? ` by ${t.resolved_by}` : ""}
            </>
          )}
          {t.resolution_notes && (
            <div className="mt-0.5 whitespace-pre-wrap text-secondary-700">
              {t.resolution_notes}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export const TicketHistoryCard: React.FC<{ scu: string }> = ({ scu }) => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState<BoxTicketHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBoxTickets(scu, token)
      .then((d) => {
        if (!cancelled) setTickets(d.tickets);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof BoxDetailApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "unknown error",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scu, token]);

  const active = (tickets ?? []).filter(
    (t) => t.status === "open" || t.status === "in_progress",
  );
  const resolved = (tickets ?? []).filter(
    (t) => t.status !== "open" && t.status !== "in_progress",
  );

  return (
    <section className="bg-white border border-secondary-200 rounded-md p-4">
      <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700 mb-2">
        Maintenance history
      </h2>
      {loading && tickets === null && (
        <div className="text-xs text-secondary-500">loading…</div>
      )}
      {error && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-2 py-1 rounded">
          {error}
        </div>
      )}
      {tickets !== null && (
        <>
          {/* Active section: open + in_progress, always visible. */}
          <div className="text-xs text-secondary-700 mb-1">
            <strong>{active.length}</strong> active ticket
            {active.length === 1 ? "" : "s"}
          </div>
          {active.length === 0 ? (
            <p className="text-xs text-secondary-500 mb-2">
              No open work on this box.
            </p>
          ) : (
            <ul className="divide-y divide-secondary-100 border border-secondary-100 rounded mb-2">
              {active.map((t) => (
                <TicketRow key={`${t.ticket_origin}-${t.ticket_id}`} t={t} boxScu={scu} />
              ))}
            </ul>
          )}

          {/* Resolved/closed: collapsed by default. */}
          {resolved.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowResolved((v) => !v)}
                className="text-xs text-primary-700 hover:text-primary-900 hover:underline"
                aria-expanded={showResolved}
              >
                {showResolved
                  ? `Hide resolved history (${resolved.length}) ▴`
                  : `Show resolved history (${resolved.length}) ▾`}
              </button>
              {showResolved && (
                <ul className="mt-2 max-h-96 overflow-auto divide-y divide-secondary-100 border border-secondary-100 rounded">
                  {resolved.map((t) => (
                    <TicketRow key={`${t.ticket_origin}-${t.ticket_id}`} t={t} boxScu={scu} />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default TicketHistoryCard;
